import { OperationalControl } from './OperationalControls';

/**
 * Mirrors the backend `StatusCategory` enum. The only status-related constant shared between
 * frontend and backend; status VALUES and labels are per-Regulation data from the API.
 */
export enum StatusCategory {
    MATURITY = 'Maturity',
    NEGATIVE = 'Negative',
    NONE = 'None',
    NOT_APPLICABLE = 'Not Applicable',
    PARTIAL = 'Partial',
    POSITIVE = 'Positive',
    UNKNOWN = 'Unknown',
}

/**
 * A single status that a Regulation supports. Mirrors the backend `StatusDefinition` dataclass.
 * Stored on `Regulation.available_statuses` and used by the UI to render dropdowns, icons, and labels.
 */
export interface StatusDefinition {
    value: string;
    category: StatusCategory;
    selectable: boolean;
    order: number;
}

/** Mirrors the backend `AggregationMode` enum. Identifies which aggregation algorithm a Regulation uses. */
export enum AggregationMode {
    FFIEC_CAT = 'FFIEC CAT',
    GENERIC = 'Generic',
}

// Attributes that comprise a Regulation.
export interface Regulation {
    aggregation_mode: string;
    available_statuses: StatusDefinition[];
    name: string;
    version: string;
}

// Attributes that comprise a Compliance Requirement.
export interface ComplianceRequirement {
    regulation: string;
    identifier: string;
    managed: boolean;
    version: string;
    text?: string; // Optional because some CFR requirements are empty strings.
    assigned_control_sets: OperationalControl[][];
    last_updated?: string;
    parent_context?: string;
    status?: string;
    metadata?: Record<string, any>; // Generic metadata (e.g. examples, guidance, maturity tiers, source) parsed and displayed generically by the UI.
}

// Render Compliance Requirements in the ComplianceRequirements page.
export interface ComplianceRequirementsForRegulationResponse {
    [key: string]: ComplianceRequirementsForRegulationRow;
}

export interface ComplianceRequirementsForRegulationRow {
    attributes: ComplianceRequirement;
    children: {
        [key: string]: ComplianceRequirementsForRegulationRow;
    };
}

export interface UpdateAssignedControlRequest {
    assigned_control_sets: string[][];
}

/**
 * A Compliance Requirement associated with a Control (matches the domain object of the API).
 * Very similar to ComplianceRequirementForControlResponse except assigned_control_sets are Control IDs instead of Control objects.
 */
export interface ComplianceRequirementForControl {
    regulation: string;
    identifier: string;
    managed: boolean;
    version: string;
    control: string;
    assigned_control_sets: string[][];
    text?: string; // Optional because some CFR requirements are empty strings.
    last_assessment?: string;
    parent_context?: string;
    status?: string | null;
    overall_status?: string;
}

// TODO: UpdateAssessmentRequest which is used in `CurrentAssessment.tsx` is using this as a request object. This will be updated in a future PR.
/**
 * A Compliance Requirement associated with a Control (matches the response DTO of the API)
 * Very similar to ComplianceRequirementForControl except assigned_control_sets are Control objects instead of Control IDs.
 */
export interface ComplianceRequirementForControlResponse {
    regulation: string;
    identifier: string;
    managed: boolean;
    version: string;
    control: string;
    assigned_control_sets: OperationalControl[][];
    text?: string; // Optional because some CFR requirements are empty strings.
    last_assessment?: string;
    parent_context?: string;
    status?: string | null;
    overall_status?: string;
}

export interface OperationalControlWithStatus {
    control: OperationalControl;
    status?: string;
}

export interface ControlSetDetails {
    control_list: OperationalControlWithStatus[];
    status?: string;
}

/**
 * While other Compliance Requirement domain objects and DTOs show the status of a Compliance Requirement in a singular view, this contains all statuses that have been set via Control Assessments for a particular Compliance Requirement.
 */
export interface ComplianceRequirementWithAllControlStatusesResponse {
    regulation: string;
    identifier: string;
    text?: string; // Optional because some CFR requirements are empty strings.
    last_updated?: string;
    parent_context?: string;
    overall_status?: string;
    assigned_control_sets: ControlSetDetails[];
}

/** Status values used by the generic aggregation algorithm. */
export enum GenericStatus {
    MET = 'Met',
    MET_PARTIAL = 'Partially Met',
    NOT_MET = 'Not Met',
}

/** Looks up a StatusDefinition by its value string. Returns undefined if not found. */
export const resolveStatusDefinition = (statuses: StatusDefinition[], value: string | null | undefined): StatusDefinition | undefined => {
    if (value === null || value === undefined) {
        return undefined;
    }
    return statuses.find((s) => s.value === value);
};
